// src/contexts/StudyTimerContext.tsx
// Estado global do timer — persiste em qualquer rota

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_BASE_URL = '/api';
const LS_KEY = 'graia_active_timer';

export interface ActiveTimer {
    sessionId: string;
    startTime: Date;
    elapsed: number;
    type: string;
    subject?: string;
    customType?: string;
    color?: string;
}

interface StudyTimerContextValue {
    activeTimer: ActiveTimer | null;
    isFullScreen: boolean;
    showStopModal: boolean;

    startTimer: (type: string, subject?: string, customType?: string, color?: string) => Promise<void>;
    stopTimer: () => Promise<void>;
    openFullScreen: () => void;
    closeFullScreen: () => void;
    openStopModal: () => void;
    closeStopModal: () => void;

    // Para o Planner injetar o toast
    setToastFn: (fn: (msg: string, type: 'success' | 'error' | 'info') => void) => void;
}

const StudyTimerContext = createContext<StudyTimerContextValue | null>(null);

export const useStudyTimer = () => {
    const ctx = useContext(StudyTimerContext);
    if (!ctx) throw new Error('useStudyTimer must be used inside StudyTimerProvider');
    return ctx;
};

export const StudyTimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [showStopModal, setShowStopModal] = useState(false);
    const toastFnRef = useRef<((msg: string, type: 'success' | 'error' | 'info') => void) | null>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const showToast = (msg: string, type: 'success' | 'error' | 'info') => {
        toastFnRef.current?.(msg, type);
    };

    // ── Restaurar sessão ativa do localStorage ao montar ──────────────────
    useEffect(() => {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return;
        try {
            const saved = JSON.parse(raw);
            setActiveTimer({
                ...saved,
                startTime: new Date(saved.startTime),
                elapsed: Math.floor((Date.now() - new Date(saved.startTime).getTime()) / 1000),
            });
        } catch {
            localStorage.removeItem(LS_KEY);
        }
    }, []);

    // ── Tick do cronômetro ────────────────────────────────────────────────
    useEffect(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);

        if (activeTimer) {
            intervalRef.current = setInterval(() => {
                setActiveTimer(prev =>
                    prev
                        ? { ...prev, elapsed: Math.floor((Date.now() - prev.startTime.getTime()) / 1000) }
                        : null
                );
            }, 1000);
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTimer?.sessionId]); // ✅ intencional: só recria o interval quando MUDA a sessão, não a cada tick

    // ── Aviso ao fechar a aba ─────────────────────────────────────────────
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!activeTimer) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [activeTimer]);

    // ── Iniciar ───────────────────────────────────────────────────────────
    const startTimer = useCallback(async (
        type: string,
        subject?: string,
        customType?: string,
        color?: string
    ) => {
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/planner/study-sessions/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ type, subject, customType, color }),
            });
            if (!res.ok) throw new Error();
            const data = await res.json();

            const timer: ActiveTimer = {
                sessionId: data.session._id,
                startTime: new Date(data.session.startTime),
                elapsed: 0,
                type,
                subject,
                customType,
                color,
            };
            setActiveTimer(timer);
            localStorage.setItem(LS_KEY, JSON.stringify(timer));
            showToast('Timer iniciado', 'success');
        } catch {
            showToast('Erro ao iniciar timer', 'error');
        }
    }, []);

    // ── Parar ─────────────────────────────────────────────────────────────
    const stopTimer = useCallback(async () => {
        if (!activeTimer) return;
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(
                `${API_BASE_URL}/planner/study-sessions/${activeTimer.sessionId}/end`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({}),
                }
            );
            if (!res.ok) throw new Error();

            const h = Math.floor(activeTimer.elapsed / 3600);
            const m = Math.floor((activeTimer.elapsed % 3600) / 60);
            const s = activeTimer.elapsed % 60;
            const fmt = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
            showToast(`Sessão finalizada: ${fmt}`, 'success');

            setActiveTimer(null);
            setIsFullScreen(false);
            setShowStopModal(false);
            localStorage.removeItem(LS_KEY);
        } catch {
            showToast('Erro ao finalizar timer', 'error');
        }
    }, [activeTimer]);

    const setToastFn = useCallback((fn: (msg: string, type: 'success' | 'error' | 'info') => void) => {
        toastFnRef.current = fn;
    }, []);

    return (
        <StudyTimerContext.Provider value={{
            activeTimer,
            isFullScreen,
            showStopModal,
            startTimer,
            stopTimer,
            openFullScreen: () => setIsFullScreen(true),
            closeFullScreen: () => setIsFullScreen(false),
            openStopModal: () => setShowStopModal(true),
            closeStopModal: () => setShowStopModal(false),
            setToastFn,
        }}>
            {children}
        </StudyTimerContext.Provider>
    );
};